import { useState, useCallback, useMemo, useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import { H1, P } from "~/ui-rtk/components/ui/typography"
import { ClockIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { Button } from "~/ui-rtk/components/ui/controls"
import BrandedSearchBudgetPanel from "./components/BrandedSearchBudgetPanel"
import InputPanel from "./components/InputPanel"
import RecommendationsTable from "./components/RecommendationsTable"
import ActionBar from "./components/ActionBar"
import ApprovalModal from "./components/ApprovalModal"
import HistoryReportModal from "./components/HistoryReportModal"
import ManualEditsBar from "./components/ManualEditsBar"
import RecentChangesAckDialog from "./components/RecentChangesAckDialog"
import InitiativeTypeBlockDialog from "./components/InitiativeTypeBlockDialog"
import { poolTotals } from "./allocation"
import { useBudgetRecommendations } from "./hooks/useBudgetRecommendations"
import { useBulkBudgetApprove } from "./hooks/useBulkBudgetApprove"
import { AgendaContextBanner } from "~/ui-rtk/components/features/agenda"
import { InitiativeTypeContainer } from "~/ui-rtk/components/features/initiative-type"
import { useAnalysisPeriods } from "./hooks/useAnalysisPeriods"
import {
  useCompanyControllerGetFinancialsQuery,
  useCompanyControllerUpsertFinancialsMutation,
} from "~/ui-rtk/api/companyApi"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useRecommendedDailyBudget } from "~/ui-rtk/hooks/useRecommendedDailyBudget"
import { DEFAULT_DAILY_BUDGET_EVAL_PERIOD } from "~/ui-rtk/constants/daily-budget-eval"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import {
  DEFAULT_ANALYSIS_PERIOD,
  STALE_DATA_DAYS,
  type BudgetInputs,
} from "./types"
import {
  autoSelectPeriod,
  confidenceFor,
  daysSince,
  hasInPeriodSpend,
  hasRecentChange,
  newlyEligibleIds,
  pruneAppliedOverrides,
  summarizeRows,
} from "./budget-utils"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import {
  LocalStorageKeys,
  getLocalStorageValue,
  setLocalStorageValue,
} from "~/ui-rtk/utils/storage"

export default function BudgetRecommender() {
  const currentCompany = useCurrentCompany()
  const { data: financials, isLoading: isLoadingFinancials } =
    useCompanyControllerGetFinancialsQuery(
      { id: currentCompany?.id ?? "" },
      { skip: !currentCompany?.id },
    )
  const [upsertFinancials] = useCompanyControllerUpsertFinancialsMutation()

  // Evaluation period for the recommended daily budget: saved setting with a
  // session-local override so the toggle takes effect before the refetch lands
  const savedEvalPeriodDays =
    financials?.dailyBudgetEvalPeriodDays ?? DEFAULT_DAILY_BUDGET_EVAL_PERIOD
  const [evalPeriodOverride, setEvalPeriodOverride] = useState<number | null>(
    null,
  )
  const dailyBudgetEvalPeriodDays = evalPeriodOverride ?? savedEvalPeriodDays

  // Drop the override once the saved value catches up after the upsert refetch
  useEffect(() => {
    if (
      evalPeriodOverride !== null &&
      evalPeriodOverride === savedEvalPeriodDays
    ) {
      setEvalPeriodOverride(null)
    }
  }, [evalPeriodOverride, savedEvalPeriodDays])

  // Get the recommended daily budget (and fallback daily spend for when
  // targetDailyBudget is not set)
  const {
    recommendedDailyBudget,
    fallbackDailySpend,
    firstOrderValue,
    isLoading: isLoadingFallback,
  } = useRecommendedDailyBudget({
    targetMerType: (financials?.targetMerType as "amer" | "mer") ?? "amer",
    targetMerValue: financials?.targetMerValue ?? 0,
    targetMerScope:
      (financials?.targetMerScope as "ecomm" | "total") ?? "total",
    evalPeriodDays: dailyBudgetEvalPeriodDays,
  })

  const [inputs, setInputs] = useState<BudgetInputs>({
    targetDRRoas: 0,
    targetBrandRoas: 0,
    targetDailyBudget: 0,
    avgOrderValue: 0,
  })
  const [hasInitialized, setHasInitialized] = useState(false)

  // Initialize inputs from Financial Settings once loaded
  useEffect(() => {
    if (hasInitialized || isLoadingFinancials) return
    if (!financials && !isLoadingFinancials) {
      // No financials saved yet — use fallback
      setInputs({
        targetDRRoas: 3.0,
        targetBrandRoas: 2.0,
        targetDailyBudget: fallbackDailySpend || 10000,
        avgOrderValue: 0,
      })
      if (!isLoadingFallback) setHasInitialized(true)
      return
    }
    if (financials) {
      setInputs({
        targetDRRoas: financials.targetDRRoas || 3.0,
        targetBrandRoas: financials.targetBrandRoas || 2.0,
        targetDailyBudget:
          financials.targetDailyBudget || fallbackDailySpend || 10000,
        avgOrderValue: financials.avgOrderValue ?? 0,
      })
      if (!isLoadingFallback || financials.targetDailyBudget) {
        setHasInitialized(true)
      }
    }
  }, [
    financials,
    isLoadingFinancials,
    hasInitialized,
    fallbackDailySpend,
    isLoadingFallback,
  ])

  // Debounced save back to Financial Settings
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    },
    [],
  )

  const handleInputsChange = useCallback(
    (newInputs: BudgetInputs) => {
      setInputs(newInputs)
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = setTimeout(async () => {
        if (!currentCompany?.id) return
        try {
          await upsertFinancials({
            id: currentCompany.id,
            body: {
              targetDRRoas: newInputs.targetDRRoas,
              targetBrandRoas: newInputs.targetBrandRoas,
              targetDailyBudget: newInputs.targetDailyBudget,
              avgOrderValue: newInputs.avgOrderValue,
            },
          }).unwrap()
        } catch (e) {
          console.error("Failed to save financial settings:", e)
        }
      }, 1500)
    },
    [currentCompany, upsertFinancials],
  )

  /** Persist an evaluation period change right away (a toggle click is a
   *  discrete action — no need for the typing debounce) */
  const handleDailyBudgetEvalPeriodChange = useCallback(
    (days: number) => {
      setEvalPeriodOverride(days)
      if (!currentCompany?.id) return
      upsertFinancials({
        id: currentCompany.id,
        body: { dailyBudgetEvalPeriodDays: days },
      })
        .unwrap()
        .catch(e => {
          console.error("Failed to save daily budget evaluation period:", e)
          // Revert the optimistic override — otherwise the UI keeps showing
          // an un-persisted selection that would silently flip back on reload
          setEvalPeriodOverride(null)
          toast.error("Failed to save evaluation period")
        })
    },
    [currentCompany, upsertFinancials],
  )

  // When the daily budget is locked to the recommendation, keep it tracking
  // the recommendation as the evaluation period (or underlying data) changes.
  useEffect(() => {
    if (!hasInitialized) return
    if (!financials?.targetDailyBudgetLocked) return
    if (recommendedDailyBudget === null || recommendedDailyBudget <= 0) return
    if (inputs.targetDailyBudget === recommendedDailyBudget) return
    handleInputsChange({ ...inputs, targetDailyBudget: recommendedDailyBudget })
  }, [
    hasInitialized,
    financials?.targetDailyBudgetLocked,
    recommendedDailyBudget,
    inputs,
    handleInputsChange,
  ])

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [editOverrides, setEditOverrides] = useState<Map<string, number>>(
    new Map(),
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)

  const {
    effectivePeriods,
    setPeriod,
    setAllPeriods,
    setPeriods,
    savePeriod,
    isDirty: isPeriodDirty,
  } = useAnalysisPeriods()

  // Selection feeds the allocation: an unchecked row stays at its current
  // budget and, when active, commits that budget to the pool before the
  // checked rows are scaled. Until the default selection lands (every
  // eligible row checked, see below) no selection is applied, so the table
  // shows the all-in recommendation rather than a sea of "as is".
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false)

  const {
    recommendations,
    allocation,
    isLoading,
    hasMissingData,
    hasUncategorizedAdsets,
    labeledCount,
    recentChangeWindowDays,
    brandSpendPct,
    latestCompleteDate,
    freshnessUnavailable,
    loadErrors,
    performanceMetrics,
  } = useBudgetRecommendations(inputs, effectivePeriods, {
    selectedIds: hasInitializedSelection ? selectedIds : null,
  })

  const { approve, progress: approveProgress } = useBulkBudgetApprove()

  // Hide rows with no spend in their analysis period (default on): a large
  // account carries hundreds of dormant ad sets that bury the rows that
  // matter and make the table slow to render. Persisted per browser.
  const [hideZeroSpend, setHideZeroSpend] = useState<boolean>(
    () =>
      getLocalStorageValue<boolean>(
        LocalStorageKeys.BUDGET_RECOMMENDER_HIDE_ZERO_SPEND,
      ) ?? true,
  )
  const handleHideZeroSpendChange = useCallback((next: boolean) => {
    setHideZeroSpend(next)
    setLocalStorageValue(
      LocalStorageKeys.BUDGET_RECOMMENDER_HIDE_ZERO_SPEND,
      next,
    )
  }, [])

  const visibleRows = useMemo(
    () =>
      hideZeroSpend
        ? recommendations.filter(hasInPeriodSpend)
        : recommendations,
    [recommendations, hideZeroSpend],
  )
  const hiddenRowCount = recommendations.length - visibleRows.length

  // Selection follows the view: a row that leaves the table (filter toggled
  // on, or its period moved to a window with no spend) can't stay selected,
  // or Review would apply changes to rows the user can't see. A row that
  // becomes locked (just pinned) leaves the selection the same way.
  useEffect(() => {
    setSelectedIds(prev => {
      const visible = new Set(
        visibleRows.filter(r => !r.lock).map(r => r.entityId),
      )
      const next = new Set([...prev].filter(id => visible.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [visibleRows])

  // Totals for the always-visible bottom bar (what the user sees, edits included)
  // Only the edits on CHECKED rows are live: an unchecked row shows and
  // applies its current budget, whatever edit it may still carry for a later
  // retry (a failed row keeps its edit). The table and the bar see this map,
  // so their totals agree with the pool line.
  const selectedOverrides = useMemo(() => {
    const next = new Map<string, number>()
    for (const [entityId, value] of editOverrides) {
      if (selectedIds.has(entityId)) next.set(entityId, value)
    }
    return next
  }, [editOverrides, selectedIds])

  const visibleSummary = useMemo(
    () =>
      visibleRows.length > 0
        ? summarizeRows(visibleRows, selectedOverrides)
        : null,
    [visibleRows, selectedOverrides],
  )

  // Warn when the data powering recommendations is stale (pipeline lag) — applying
  // budgets off old performance is a silent data error.
  const staleDays = useMemo(
    () => daysSince(latestCompleteDate),
    [latestCompleteDate],
  )

  const handleToggleSelect = useCallback((entityId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(entityId)) {
        next.delete(entityId)
        // An unchecked row stays at its current budget; a hand-edited value
        // on it would be neither shown nor applied, so drop it.
        setEditOverrides(edits => {
          if (!edits.has(entityId)) return edits
          const nextEdits = new Map(edits)
          nextEdits.delete(entityId)
          return nextEdits
        })
      } else {
        next.add(entityId)
      }
      return next
    })
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      // Every categorized row is selectable, recently changed ones included
      // (the review step asks for an acknowledgment on those) — except a
      // locked one (pin or brand-search policy).
      const selectable = recommendations.filter(
        r => r.category !== null && !r.lock,
      )
      if (prev.size === selectable.length) {
        return new Set()
      }
      return new Set(selectable.map(r => r.entityId))
    })
  }, [recommendations])

  /**
   * The rows a budget update starts from: categorized, active (not paused,
   * spending in their window) and with usable data. Paused and zero-spend
   * rows are left unchecked — the recommender would only ever pause them —
   * and so are missing-data rows, whose $0 recommendation is a data hole,
   * and locked rows (a pin or the brand-search policy), which cannot move.
   */
  const defaultSelectableIds = useCallback(
    () =>
      recommendations
        .filter(
          r =>
            r.category !== null && r.isActive && !r.hasMissingData && !r.lock,
        )
        .map(r => r.entityId),
    [recommendations],
  )

  // Default selection: every eligible row checked once the rows exist. With
  // selection feeding the allocation an empty selection would read as "no
  // change" everywhere and hide the recommender's main output; all-checked
  // reproduces the familiar first-load view, and unchecking carves a row
  // out. Runs once per mount — an approve clears the selection on purpose.
  // Eligible ids seen so far: a row that becomes eligible LATER (typically
  // one that just got its initiative type after the type-block dialog sent
  // the user back to the table, or one whose widened window found spend)
  // joins the selection the way it would have on load, while rows seen
  // before keep whatever the user decided for them.
  const seenEligibleRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (isLoading || recommendations.length === 0) return
    const eligible = defaultSelectableIds()
    if (!hasInitializedSelection) {
      setSelectedIds(new Set(eligible))
      seenEligibleRef.current = new Set(eligible)
      setHasInitializedSelection(true)
      return
    }
    const fresh = newlyEligibleIds(eligible, seenEligibleRef.current)
    if (fresh.length === 0) return
    for (const id of fresh) seenEligibleRef.current.add(id)
    setSelectedIds(prev => {
      const next = new Set(prev)
      for (const id of fresh) next.add(id)
      return next
    })
  }, [
    hasInitializedSelection,
    isLoading,
    recommendations.length,
    defaultSelectableIds,
  ])

  const handleEditBudget = useCallback((entityId: string, value: number) => {
    setEditOverrides(prev => {
      const next = new Map(prev)
      next.set(entityId, value)
      return next
    })
  }, [])

  // Hand-edited budgets across every row in the data set — including rows the
  // zero-spend filter hides, since the reset clears those too. An override
  // whose row has since dropped out of the data set is not counted.
  const manualEditCount = useMemo(
    () => recommendations.filter(r => editOverrides.has(r.entityId)).length,
    [recommendations, editOverrides],
  )

  // Drop every hand-edited budget so each row shows its calculated
  // recommendation again. Overrides are session state only, so nothing is
  // written anywhere — a value that was already approved is the platform's
  // live budget now and comes back through the Current Budget column.
  const handleResetEdits = useCallback(() => {
    setEditOverrides(new Map())
    if (manualEditCount > 0) {
      toast.success(
        `Cleared ${manualEditCount} manual budget ${
          manualEditCount === 1 ? "edit" : "edits"
        }`,
      )
    }
  }, [manualEditCount])

  const handlePeriodChange = useCallback(
    (entityId: string, days: number) => {
      setPeriod(entityId, days)
      // A hand-edited budget against a different window is misleading — clear it
      setEditOverrides(prev => {
        if (!prev.has(entityId)) return prev
        const next = new Map(prev)
        next.delete(entityId)
        return next
      })
    },
    [setPeriod],
  )

  // One-time apply of a period to every row. Not a lock — rows stay
  // individually changeable until the global dropdown is changed again.
  const [globalAnalysisPeriod, setGlobalAnalysisPeriod] = useState(
    DEFAULT_ANALYSIS_PERIOD,
  )
  const handleGlobalPeriodChange = useCallback(
    (days: number) => {
      setGlobalAnalysisPeriod(days)
      setAllPeriods(
        recommendations.map(r => r.entityId),
        days,
      )
      // Same rule as the per-row change: drop hand-edited budgets for rows
      // whose analysis window actually changed
      setEditOverrides(prev => {
        const changed = recommendations.filter(
          r => prev.has(r.entityId) && r.analysisPeriodDays !== days,
        )
        if (changed.length === 0) return prev
        const next = new Map(prev)
        for (const r of changed) next.delete(r.entityId)
        return next
      })
    },
    [recommendations, setAllPeriods],
  )

  // Auto-select by confidence: every categorized row moves to the shortest
  // analysis period that reaches High confidence (autoSelectPeriod spells
  // out the fallback). Session-only like the global dropdown; the per-row
  // save persists a pick. Applies to hidden rows too, since a row with no
  // spend at 7d may well have High confidence at 30d.
  const handleAutoSelectPeriods = useCallback(() => {
    const updates = new Map<string, number>()
    let belowHigh = 0
    let skipped = 0
    for (const r of recommendations) {
      if (r.category === null) continue
      const pick = autoSelectPeriod(r.estimatedConversionsByPeriod)
      if (pick === null) {
        skipped += 1
        continue
      }
      if (
        confidenceFor(r.estimatedConversionsByPeriod[pick] ?? null) !== "HIGH"
      ) {
        belowHigh += 1
      }
      if (pick !== r.analysisPeriodDays) updates.set(r.entityId, pick)
    }
    if (updates.size > 0) {
      setPeriods(updates)
      // Same rule as any period change: a hand-edited budget against a
      // different window is misleading, so drop those edits
      setEditOverrides(prev => {
        const next = new Map(prev)
        for (const entityId of updates.keys()) next.delete(entityId)
        return next.size === prev.size ? prev : next
      })
    }
    const notes: string[] = []
    if (belowHigh > 0) {
      notes.push(`${belowHigh} never reach High and use their best window`)
    }
    if (skipped > 0) {
      notes.push(`${skipped} skipped (no conversions to evaluate)`)
    }
    const suffix = notes.length > 0 ? ` — ${notes.join("; ")}` : ""
    toast.success(
      updates.size === 0
        ? `Every row already uses its best analysis period${suffix}`
        : `Updated ${updates.size} ${updates.size === 1 ? "row" : "rows"} to the shortest High-confidence period${suffix}`,
    )
  }, [recommendations, setPeriods])

  // Get selected rows for the modal
  const selectedRows = useMemo(
    () =>
      visibleRows.filter(
        r => selectedIds.has(r.entityId) && r.category !== null,
      ),
    [visibleRows, selectedIds],
  )

  // Active rows the recommender cannot place: no initiative type, or a CBO
  // campaign whose spending ad sets lack one. The review is gated on them —
  // unassigned spend silently distorts every other row's share of the
  // target — unless the user explicitly overrides the gate, since the check
  // can flag a campaign the brand did type.
  const unassignedActiveRows = useMemo(
    () =>
      recommendations.filter(
        r => r.isActive && (r.category === null || r.hasUncategorizedAdsets),
      ),
    [recommendations],
  )

  // The rest of the pool: active rows left at their current budget. That
  // is the unchecked categorized rows plus, once the type gate has been
  // overridden, the rows with no type at all (never selectable; they sit in
  // the DR share). The review lists them, the CSV exports them, the batch
  // audit records them as skipped, and the recap justifies them.
  const unchangedActiveRows = useMemo(
    () =>
      recommendations.filter(
        r =>
          r.isActive && (r.category === null || !selectedIds.has(r.entityId)),
      ),
    [recommendations, selectedIds],
  )

  // Selected rows carrying a recent-change label: the review opens only
  // after the user acknowledges them.
  const labeledSelectedRows = useMemo(
    () => selectedRows.filter(hasRecentChange),
    [selectedRows],
  )

  const [isTypeBlockOpen, setIsTypeBlockOpen] = useState(false)
  const [isAckOpen, setIsAckOpen] = useState(false)

  // Past the initiative-type gate: the recent-change acknowledgment, then
  // the review itself.
  const openReviewPastTypeGate = useCallback(() => {
    if (labeledSelectedRows.length > 0) {
      setIsAckOpen(true)
      return
    }
    setIsModalOpen(true)
  }, [labeledSelectedRows.length])

  // The gate in front of the review: initiative types first (blocking
  // unless overridden), then the recent-change acknowledgment, then the
  // review itself.
  const handleReview = useCallback(() => {
    if (unassignedActiveRows.length > 0) {
      setIsTypeBlockOpen(true)
      return
    }
    openReviewPastTypeGate()
  }, [unassignedActiveRows.length, openReviewPastTypeGate])

  // The user acknowledged the missing initiative types and chose to update
  // budgets anyway. Asked again on every review, like the recent-change
  // acknowledgment: a fresh review is a fresh decision.
  const handleTypeBlockOverride = useCallback(() => {
    setIsTypeBlockOpen(false)
    openReviewPastTypeGate()
  }, [openReviewPastTypeGate])

  const handleAckConfirm = useCallback(() => {
    setIsAckOpen(false)
    setIsModalOpen(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  // The pool as it will be applied, hand edits included — what the bottom
  // bar and the review summary show.
  const pool = useMemo(
    () =>
      hasInitializedSelection
        ? poolTotals(
            recommendations,
            new Set(
              recommendations
                .filter(
                  r => r.category !== null && !selectedIds.has(r.entityId),
                )
                .map(r => r.entityId),
            ),
            editOverrides,
            inputs.targetDailyBudget,
          )
        : null,
    [
      hasInitializedSelection,
      recommendations,
      selectedIds,
      editOverrides,
      inputs.targetDailyBudget,
    ],
  )

  const handleApprove = useCallback(
    async (
      rows: import("./types").RecommendationRow[],
      overrides: Map<string, number>,
      smartScale?: import("./components/ApprovalModal").SmartScalePlan,
      unchangedRows?: import("./types").RecommendationRow[],
    ) => {
      const results = await approve(
        rows,
        overrides,
        inputs,
        smartScale,
        unchangedRows,
      )
      // Clear selection after approval
      setSelectedIds(new Set())
      // An applied edit is the live budget now, not a pending change — drop it
      // so the row stops reading as hand-edited. Failed rows keep theirs.
      setEditOverrides(prev => pruneAppliedOverrides(prev, results))
      return results
    },
    [approve, inputs],
  )

  const handleCancel = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  // Deep-link entry (e.g. from a budget update reminder): pre-select the
  // default rows and open the review so the user can apply recommended
  // changes in one step — through the same gate as the Review button, so a
  // missing initiative type or a recent change is never skipped past.
  const [searchParams, setSearchParams] = useSearchParams()
  const hasHandledDeepLinkRef = useRef(false)
  useEffect(() => {
    if (hasHandledDeepLinkRef.current) return
    if (searchParams.get("review") !== "1") return
    if (isLoading || recommendations.length === 0) return
    if (!hasInitializedSelection) return

    // With freshness tracking unavailable the rows are computed against an
    // assumed anchor, and the modal would cover the warning banner at the
    // exact moment the user is asked to approve — never auto-open then; the
    // user can still review manually with the banner visible.
    if (!freshnessUnavailable) {
      // The default selection (every eligible row) is already in place and
      // the gate's memos are settled, so this is exactly a Review click.
      handleReview()
    }
    hasHandledDeepLinkRef.current = true

    // Strip the param so refresh/close doesn't reopen the modal.
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete("review")
        return next
      },
      { replace: true },
    )
  }, [
    searchParams,
    isLoading,
    recommendations,
    hasInitializedSelection,
    freshnessUnavailable,
    handleReview,
    setSearchParams,
  ])

  return (
    <InitiativeTypeContainer>
      <div className="h-full px-system-md py-system-sm pb-24">
        <div className="mb-system-lg flex items-start justify-between gap-4">
          <div>
            <H1 className="text-white font-medium">Budget Recommender</H1>
            <P className="text-gray-400 text-sm">
              Optimize campaign budgets across channels based on ROAS
              performance and allocation targets
            </P>
          </div>
          {/* Past budget / tROAS changes for this company, any date range. */}
          <Button
            variant={{ variant: "outlined" }}
            size="sm"
            type="button"
            className="flex shrink-0 whitespace-nowrap"
            onClick={() => setIsHistoryOpen(true)}
          >
            <ClockIcon className="h-4 w-4" />
            History report
          </Button>
        </div>

        <AgendaContextBanner className="mb-system-md" />

        <div className="space-y-6">
          <InputPanel
            inputs={inputs}
            onChange={handleInputsChange}
            isLoading={!hasInitialized}
            settingsLink={brandRoutes.SETUP.FINANCIAL_SETTINGS}
            globalAnalysisPeriod={globalAnalysisPeriod}
            onGlobalAnalysisPeriodChange={handleGlobalPeriodChange}
            globalAnalysisPeriodDisabled={isLoading}
            dailyBudgetEvalPeriod={dailyBudgetEvalPeriodDays}
            onDailyBudgetEvalPeriodChange={handleDailyBudgetEvalPeriodChange}
            recommendedDailyBudget={recommendedDailyBudget}
            onAutoSelectAnalysisPeriods={handleAutoSelectPeriods}
            lockState={{
              targetDRRoasLocked: financials?.targetDRRoasLocked ?? false,
              targetBrandRoasLocked: financials?.targetBrandRoasLocked ?? false,
              targetDailyBudgetLocked:
                financials?.targetDailyBudgetLocked ?? false,
            }}
          />

          {/* Branded search is budgeted on its own click-yield curve, not the
              ROAS allocation above; the panel renders only when the company
              has BRANDED_SEARCH-typed Google campaigns. */}
          <BrandedSearchBudgetPanel
            rows={recommendations}
            onApply={(rows, overrides) => handleApprove(rows, overrides)}
            settingsLink={brandRoutes.SETUP.FINANCIAL_SETTINGS}
          />

          {freshnessUnavailable && (
            <div className="flex items-center gap-2 bg-yellow-900/30 border border-yellow-700 rounded-lg px-4 py-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 shrink-0" />
              <span className="text-yellow-300 text-sm">
                Data freshness tracking is unavailable for this company, so
                recommendations assume data through {latestCompleteDate}. Verify
                the &quot;Data Through&quot; dates before applying budgets.
              </span>
            </div>
          )}

          {staleDays !== null && staleDays > STALE_DATA_DAYS && (
            <div className="flex items-center gap-2 bg-yellow-900/30 border border-yellow-700 rounded-lg px-4 py-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 shrink-0" />
              <span className="text-yellow-300 text-sm">
                Latest performance data is {staleDays} days old (through{" "}
                {latestCompleteDate}). Recommendations are based on stale data —
                verify before applying budgets.
              </span>
            </div>
          )}

          {hasMissingData && (
            <div className="flex items-center gap-2 bg-yellow-900/30 border border-yellow-700 rounded-lg px-4 py-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 shrink-0" />
              <span className="text-yellow-300 text-sm">
                Some DR entities have spend but no modeled revenue data. These
                are excluded from bulk selection and never auto-paused — review
                and set their budgets manually.
              </span>
            </div>
          )}

          {labeledCount > 0 && (
            <div className="flex items-center gap-2 bg-amber-900/20 border border-amber-800 rounded-lg px-4 py-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-300 shrink-0" />
              <span className="text-amber-200 text-sm">
                {labeledCount}{" "}
                {labeledCount === 1 ? "entity had" : "entities had"} a recent
                change: a person or agent changed their budget or bid target, or
                enabled them, or they first spent, within the last{" "}
                {recentChangeWindowDays ?? 7} days (the recent change window in
                Financial Settings). Look for the amber and blue badges. They
                are included like any other row — uncheck one to leave it as is
                — and you will be asked to confirm before changes are applied to
                them.
              </span>
            </div>
          )}

          {hasUncategorizedAdsets && (
            <div className="flex items-center gap-2 bg-yellow-900/30 border border-yellow-700 rounded-lg px-4 py-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 shrink-0" />
              <span className="text-yellow-300 text-sm">
                Some CBO campaigns have ad sets without initiative type
                assignments. These campaigns are categorized by the majority
                type of their assigned ad sets. Look for the warning icon in the
                table.
              </span>
            </div>
          )}

          {loadErrors.length > 0 ? (
            <div className="flex items-center gap-2 border border-states-error rounded-lg px-4 py-6">
              <ExclamationTriangleIcon className="w-5 h-5 text-states-error shrink-0" />
              <span className="text-states-error text-sm">
                Couldn&apos;t load {loadErrors.join(", ")}. Recommendations
                can&apos;t be computed from partial data — this is a loading
                error, not missing campaign spend. Refresh the page to retry.
              </span>
            </div>
          ) : isLoading ? (
            <div className="border border-basic-blue rounded-lg p-8">
              <div className="animate-pulse space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-10 bg-basic-dark-blue rounded" />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {recommendations.length > 0 && (
                <div className="flex items-center justify-between gap-4 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer text-gray-300">
                    <Checkbox
                      checked={hideZeroSpend}
                      onChange={e =>
                        handleHideZeroSpendChange(e.target.checked)
                      }
                      className="checkbox-sm"
                    />
                    Hide rows with no spend in their analysis period
                  </label>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-400">
                      Showing {visibleRows.length} of {recommendations.length}{" "}
                      rows
                    </span>
                    <ManualEditsBar
                      manualEditCount={manualEditCount}
                      onReset={handleResetEdits}
                    />
                  </div>
                </div>
              )}
              <RecommendationsTable
                rows={visibleRows}
                selectedIds={selectedIds}
                editOverrides={selectedOverrides}
                latestCompleteDate={latestCompleteDate}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                onEditBudget={handleEditBudget}
                onChangePeriod={handlePeriodChange}
                onSavePeriod={savePeriod}
                isPeriodDirty={isPeriodDirty}
                emptyMessage={
                  hiddenRowCount > 0
                    ? `All ${recommendations.length} rows have no spend in their analysis period. Uncheck the filter above to show them.`
                    : undefined
                }
              />
            </div>
          )}
        </div>

        <ActionBar
          selectedCount={selectedRows.length}
          summary={visibleSummary}
          hiddenRowCount={hiddenRowCount}
          allocation={recommendations.length > 0 ? allocation : null}
          pool={pool}
          onReview={handleReview}
          onCancel={handleCancel}
        />

        <InitiativeTypeBlockDialog
          isOpen={isTypeBlockOpen}
          rows={unassignedActiveRows}
          onClose={() => setIsTypeBlockOpen(false)}
          onOverride={handleTypeBlockOverride}
        />

        <RecentChangesAckDialog
          isOpen={isAckOpen}
          rows={labeledSelectedRows}
          windowDays={recentChangeWindowDays}
          onCancel={() => setIsAckOpen(false)}
          onConfirm={handleAckConfirm}
        />

        <ApprovalModal
          isOpen={isModalOpen}
          rows={selectedRows}
          unchangedRows={unchangedActiveRows}
          editOverrides={editOverrides}
          brandSpendPct={brandSpendPct}
          targetDailyBudget={inputs.targetDailyBudget}
          targetDRRoas={inputs.targetDRRoas}
          targetBrandRoas={inputs.targetBrandRoas}
          allocation={allocation}
          pool={pool}
          performanceMetrics={performanceMetrics}
          firstOrderValue={firstOrderValue}
          progress={approveProgress}
          onClose={handleModalClose}
          onApprove={handleApprove}
        />

        <HistoryReportModal
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
        />
      </div>
    </InitiativeTypeContainer>
  )
}
